import React, { useState, useEffect, useMemo } from 'react';
import MathText from '../../VueQuestion/MathText';
import { X, Calendar, Clock, Palette, BookOpen, Target, Trash2, Sparkles, AlertCircle } from 'lucide-react';

function EditSequenceNiveau({ isOpen, onClose, sequence, themes, onSave, isLoading = false }) {
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [savoirFaires, setSavoirFaires] = useState([]);
  const [attendus, setAttendus] = useState([]);

  // Initialiser les valeurs quand la séquence change
  useEffect(() => {
    if (sequence) {
      setTitle(sequence.title || '');
      setStartDate(sequence.start ? sequence.start.split('T')[0] : '');
      setEndDate(sequence.end ? sequence.end.split('T')[0] : '');
      setSelectedTheme(sequence.theme_for_sequence || '');
      setSavoirFaires(sequence.savoir_faires_details || []);
      setAttendus(sequence.attendus_details || []);
    }
  }, [sequence]);

  // Calculer la durée en semaines
  const calculateDuration = () => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.ceil(diffDays / 7);
    return `${weeks} semaine${weeks > 1 ? 's' : ''}`;
  };

  // Validation des erreurs
  const validationErrors = useMemo(() => {
    const errors = [];

    if (!title.trim()) {
      errors.push("Le titre de la séquence est obligatoire");
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      errors.push("La date de fin doit être postérieure à la date de début");
    }

    return errors;
  }, [title, startDate, endDate]);

  const isFormValid = validationErrors.length === 0 && title.trim();

  // Détecter si des modifications ont été apportées
  const hasChanges = useMemo(() => {
    if (!sequence) return false;

    const originalTitle = sequence.title || '';
    const originalStartDate = sequence.start ? sequence.start.split('T')[0] : '';
    const originalEndDate = sequence.end ? sequence.end.split('T')[0] : '';
    const originalTheme = sequence.theme_for_sequence || '';
    const originalSavoirFaireIds = (sequence.savoir_faires_details || []).map(sf => sf.id).sort();
    const originalAttenduIds = (sequence.attendus_details || []).map(att => att.id).sort();

    const currentSavoirFaireIds = savoirFaires.map(sf => sf.id).sort();
    const currentAttenduIds = attendus.map(att => att.id).sort();

    // Comparer chaque champ
    if (title !== originalTitle) return true;
    if (startDate !== originalStartDate) return true;
    if (endDate !== originalEndDate) return true;
    if (selectedTheme !== originalTheme) return true;
    if (JSON.stringify(currentSavoirFaireIds) !== JSON.stringify(originalSavoirFaireIds)) return true;
    if (JSON.stringify(currentAttenduIds) !== JSON.stringify(originalAttenduIds)) return true;

    return false;
  }, [sequence, title, startDate, endDate, selectedTheme, savoirFaires, attendus]);

  // Supprimer un savoir-faire
  const handleRemoveSavoirFaire = (id) => {
    setSavoirFaires(prev => prev.filter(sf => sf.id !== id));
  };

  // Supprimer un attendu
  const handleRemoveAttendu = (id) => {
    setAttendus(prev => prev.filter(att => att.id !== id));
  };

  // Sauvegarder les modifications
  const handleSave = () => {
    // Trouver l'ID du thème sélectionné
    const selectedThemeObj = themes?.find(t => t.nom === selectedTheme);

    const payload = {
      title,
      start: startDate,
      end: endDate,
      savoir_faires: savoirFaires.map(sf => sf.id),
      attendus: attendus.map(att => att.id)
    };

    // Le thème n'est envoyé QUE s'il a été résolu. L'ancien
    // `theme: selectedThemeObj?.id || null` traduisait « je n'ai pas trouvé »
    // en « il n'y en a pas » : dès que la liste des thèmes était vide (ex. 5e
    // français pas encore livrée) ou en cours de rechargement, corriger un
    // simple titre effaçait le rattachement — et une séquence sans thème
    // disparaît de la frise de TOUS les profs (filtre serveur theme__matiere).
    // La requête est un PATCH : une clé absente laisse la valeur intacte.
    if (selectedThemeObj) payload.theme = selectedThemeObj.id;

    onSave(payload);
  };

  const handleClose = () => {
    // Réinitialiser les états aux valeurs originales de la séquence
    if (sequence) {
      setTitle(sequence.title || '');
      setStartDate(sequence.start ? sequence.start.split('T')[0] : '');
      setEndDate(sequence.end ? sequence.end.split('T')[0] : '');
      setSelectedTheme(sequence.theme_for_sequence || '');
      setSavoirFaires(sequence.savoir_faires_details || []);
      setAttendus(sequence.attendus_details || []);
    } else {
      setTitle('');
      setStartDate('');
      setEndDate('');
      setSelectedTheme('');
      setSavoirFaires([]);
      setAttendus([]);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-blue-500/20 w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/50">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-base font-semibold text-slate-800">Modification en cours...</p>
            <p className="text-sm text-slate-500 mt-1">Veuillez patienter</p>
          </div>
        )}

        {/* Header avec glassmorphism */}
        <div className="relative overflow-hidden">
          {/* Glow effect */}
          <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400 rounded-3xl blur-2xl opacity-30 animate-pulse" />

          <div className="relative overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-sky-500 to-cyan-400" />

            {/* Animated blobs */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 200" preserveAspectRatio="none">
              <defs>
                <linearGradient id="edit-blob1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#c4b5fd" stopOpacity="0.2" />
                </linearGradient>
                <linearGradient id="edit-blob2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#67e8f9" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.1" />
                </linearGradient>
              </defs>

              <path fill="url(#edit-blob1)">
                <animate attributeName="d" dur="8s" repeatCount="indefinite"
                  values="M0,100 Q200,50 400,80 Q600,120 800,70 L800,200 L0,200 Z;
                          M0,70 Q200,110 400,60 Q600,80 800,120 L800,200 L0,200 Z;
                          M0,100 Q200,50 400,80 Q600,120 800,70 L800,200 L0,200 Z" />
              </path>

              <path fill="url(#edit-blob2)">
                <animate attributeName="d" dur="6s" repeatCount="indefinite"
                  values="M0,130 Q300,90 500,110 Q700,140 800,95 L800,200 L0,200 Z;
                          M0,95 Q300,130 500,90 Q700,105 800,140 L800,200 L0,200 Z;
                          M0,130 Q300,90 500,110 Q700,140 800,95 L800,200 L0,200 Z" />
              </path>

              <circle cx="150" cy="80" r="2" fill="white" opacity="0.4">
                <animate attributeName="cy" dur="3s" values="80;50;80" repeatCount="indefinite" />
              </circle>
              <circle cx="650" cy="100" r="2" fill="white" opacity="0.3">
                <animate attributeName="cy" dur="4s" values="100;65;100" repeatCount="indefinite" />
              </circle>
            </svg>

            {/* Header content */}
            <div className="relative px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 backdrop-blur rounded-xl border border-white/30 shadow-xl">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Éditer la séquence
                  </h2>
                  <p className="text-white/80 text-sm">
                    Modifier les informations de la séquence
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 bg-orange-500/80 hover:bg-orange-600 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-280px)]">
          {/* Titre */}
          <div className="mb-5">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Titre de la séquence
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm text-slate-800
                focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                transition-all duration-300 bg-white/80"
              placeholder="Ex: Nombres relatifs"
            />
          </div>

          {/* Thème */}
          <div className="mb-5">
            <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <Palette className="w-4 h-4 text-purple-500" />
              Thème
            </label>
            <select
              value={selectedTheme}
              onChange={(e) => setSelectedTheme(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm text-slate-800
                focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                transition-all duration-300 bg-white/80 cursor-pointer"
            >
              <option value="">Sélectionner un thème</option>
              {themes && themes.map((theme) => (
                <option key={theme.id} value={theme.nom}>
                  {theme.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                Date de début
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm text-slate-800
                  focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                  transition-all duration-300 bg-white/80"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                Date de fin
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm text-slate-800
                  focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                  transition-all duration-300 bg-white/80"
              />
            </div>
          </div>

          {/* Durée calculée */}
          {startDate && endDate && (
            <div className="mb-6 p-3 bg-purple-50 border border-purple-200 rounded-xl flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-medium text-purple-700">
                Durée estimée : {calculateDuration()}
              </span>
            </div>
          )}

          {/* Savoir-faires */}
          {savoirFaires.length > 0 && (
            <div className="mb-5">
              <label className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-blue-500" />
                Savoir-faires ({savoirFaires.length})
              </label>
              {/* Pas d'overflow propre : le SEUL scroll est celui du corps
                  du modal (double scrollbar signalée prof 2026-08-25) */}
              <div className="space-y-2">
                {savoirFaires.map((sf) => (
                  <div
                    key={sf.id}
                    className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-xl group"
                  >
                    <span className="text-sm text-slate-700 flex-1 pr-2">{sf.nom}</span>
                    <button
                      onClick={() => handleRemoveSavoirFaire(sf.id)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attendus */}
          {attendus.length > 0 && (
            <div className="mb-5">
              <label className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-500" />
                Attendus ({attendus.length})
              </label>
              <div className="space-y-2">
                {attendus.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-xl group"
                  >
                    <span className="text-sm text-slate-700 flex-1 pr-2"><MathText>{att.description}</MathText></span>
                    <button
                      onClick={() => handleRemoveAttendu(att.id)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message si pas de savoirs ni attendus */}
          {savoirFaires.length === 0 && attendus.length === 0 && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center">
              <p className="text-sm text-slate-500">
                Aucun savoir-faire ou attendu associé à cette séquence.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Utilisez le bouton "Ajouter un savoir" depuis la carte séquence pour en ajouter.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50/80">
          {/* Affichage des erreurs de validation */}
          {validationErrors.length > 0 && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-xl">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  {validationErrors.map((error, index) => (
                    <p key={index} className="text-sm font-medium text-orange-700">
                      {error}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end items-center gap-3">
            <button
              onClick={handleClose}
              className="px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors"
            >
              {hasChanges ? 'Annuler' : 'Fermer'}
            </button>
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={!isFormValid}
                className={`
                  px-6 py-3 rounded-xl font-medium transition-all duration-300
                  ${isFormValid
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg hover:shadow-xl hover:scale-105'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                Enregistrer les modifications
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditSequenceNiveau;
